import React, { useEffect, useRef } from "react";
import { Box, Button, Typography } from "@material-ui/core";

import { GateCheckInStatus } from "../../../utils/constants";
import {
  gateStatusRefusal,
  gateStatusRestriction,
} from "../../../utils/gateQueueOrder";
import {
  GateDockField,
  GateDriverField,
  GatePlateField,
  GateStatusSelect,
} from "./GateCheckInFields";
import { GATE_ACTIVE_STATUSES } from "./gateCheckInUtils";
import { useGateQueueTurn } from "./useGateQueueTurn";

const { DOCKED } = GateCheckInStatus;

const isActiveGateStatus = (status) => GATE_ACTIVE_STATUSES.includes(status);

/** Inline gate-queue control for the shipment dispatch tabs: shows the
 * shipment's active check-in and lets the manager advance it through
 * ARRIVED → CALLED → DOCKED → DEPARTED (or CANCELLED) without leaving
 * the form for the Gate Queue page.
 *
 * With no driver checked in it shows how this truck gets on the gate queue
 * instead of vanishing. A driver who cannot use the kiosk — no phone, no
 * signal, no English, a QR nobody has posted at that gate yet — is checked in
 * by the office, and until this state was drawn the only way to do that was to
 * know that stamping the arrival time mints the row as a side effect of Save.
 * `manualCheckIn` is what the tab passes to drive it:
 *   - `onCheckIn` starts the check-in: it stamps the arrival the row is dated
 *     from — which is what carries the shipment to ARRIVED on the same save —
 *     through the tab's own arrival handler, so the picker and this button do
 *     one thing. It is null when the shipment may not start a gate visit at
 *     all (already past spotting, or already holding a row) — see
 *     canOfferManualGateCheckIn.
 *   - `pending` says the arrival is stamped and the save will mint the row, so
 *     the same fields open as for a real one and what the manager fills in
 *     becomes the row's opening values.
 * Nothing here writes: the row appears when the form's Save runs
 * createManualGateCheckIn, the same path a manager typing an arrival takes.
 *
 * Driver and plate are inputs, not labels, because a row the office wrote for
 * a truck that never reached the kiosk arrives with neither — the manager asks
 * the driver and fills them in here. A row a signed-in driver checked in
 * against keeps its name locked; see GateDriverField.
 *
 * A supervisor gets the same queue-order restriction the Gate Queue page
 * applies — the statuses that would call a truck forward out of turn are greyed
 * out, with the truck ahead named. A manager owns the schedule and gets neither.
 * DEPARTED is greyed for both until the truck is at a dock, with no tooltip: who
 * may call a truck forward is a matter of authority, but whether a visit
 * happened is not.
 * `queueTurn` lets a caller that already read the turn pass it in: the dispatch
 * tabs disable their Time Placed picker on the same answer (placing a load
 * means the truck reached a dock), and reading it twice per row would cost a
 * second queue query for one answer.
 *
 * The control has no save of its own: the parent dispatch tab holds the
 * pending edit in the form (gateCheckInEdit) and the form's primary Save
 * persists it together with the shipment. `checkIn` and `containerRef` come
 * from the parent's useActiveGateCheckIn, so the tab can also couple its
 * date pickers to the gate status.
 *
 * onAtDock, when given, receives a placement timestamp so the open form
 * stamps its own placed-time field rather than the control writing behind the
 * manager's back.
 *
 * `shipment` is the load as the form sees it (record under form values), for
 * the departure rule: DEPARTED is greyed on a docked row until the load has
 * an end time or is COMPLETED.
 */
export const GateCheckInControl = ({
  checkIn,
  containerRef,
  editor,
  manualCheckIn,
  onAtDock,
  queueTurn,
  shipment = null,
}) => {
  // A crew member may move the driver to DOCKED from the Gate Queue page
  // while this form sits open; the poll pulls the DOCKED row in and the form
  // still owes itself the placed stamp. Offered once per row identity so a
  // poll tick cannot re-offer over the manager's cleared field; the handler
  // no-ops against an already placed form.
  const dockStampOfferedFor = useRef(null);
  useEffect(() => {
    if (!onAtDock || !checkIn || checkIn.status !== DOCKED) return;
    if (dockStampOfferedFor.current === checkIn.id) return;
    dockStampOfferedFor.current = checkIn.id;
    onAtDock(new Date().toISOString());
  }, [checkIn, onAtDock]);

  // A supervisor calls drivers in the published order, and this tab shows one
  // truck with no queue behind it — so the queue is read for it. Without this
  // the select here offered a call the Gate Queue page greys out, and the
  // refusal only arrived once the save had run.
  // Called with null when the caller supplied the answer, so the hook stays
  // unconditional without running the query a second time.
  const ownTurn = useGateQueueTurn(queueTurn ? null : checkIn);
  const { orderBound, isNextInQueue, nextLabel } = queueTurn || ownTurn;
  const isStatusAllowed = (status) =>
    !gateStatusRefusal({
      status,
      currentStatus: checkIn?.status,
      orderBound,
      isNextInQueue,
      shipment,
    });

  // A row the save will mint wears the same fields as a real one: what the
  // manager fills in becomes its opening values, so the office writes down the
  // driver it just waved in instead of saving, waiting for the row, and coming
  // back for a second pass.
  const pendingRow = !checkIn && Boolean(manualCheckIn?.pending);

  // Every branch below keeps containerRef attached: the ref lets the poll skip
  // ticks while this control sits on a hidden form tab (react-admin keeps
  // inactive tabs mounted but display:none), and no driver checked in is the
  // state that would otherwise poll a hidden tab forever.
  if (!checkIn && !pendingRow) {
    // Nothing at the gate and nothing coming. A caller that passes no
    // manualCheckIn (the Gate Queue's own uses, and every caller predating it)
    // keeps the bare anchor.
    if (!manualCheckIn?.onCheckIn) return <span ref={containerRef} />;
    return (
      // minHeight matches the dense outlined fields this row is replacing (40
      // high, 8 above, 4 below) so the empty state sits on the same line as
      // the pickers beside it rather than riding up against the row above.
      <Box
        ref={containerRef}
        display="flex"
        alignItems="center"
        gridGap={12}
        flexWrap="wrap"
        minHeight={52}
      >
        <Typography variant="body2" color="textSecondary">
          No driver checked in at the gate.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={manualCheckIn.onCheckIn}
        >
          Check In Truck
        </Button>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      display="flex"
      alignItems="center"
      gridGap={12}
      flexWrap="wrap"
    >
      <GateDriverField
        editor={editor}
        readOnly={Boolean(checkIn?.carrierUserId)}
      />
      <GatePlateField editor={editor} />
      <GateStatusSelect
        editor={editor}
        // A row that does not exist yet can only be minted at a status the
        // truck is actually at; ending a visit that never started would write
        // a completed row for a truck standing at the gate.
        // Always asked, for every user type: the queue order binds only a
        // supervisor, but a visit that never reached a dock cannot be
        // completed by anyone.
        isStatusAllowed={pendingRow ? isActiveGateStatus : isStatusAllowed}
        // A row with no status yet has its own reason for what it greys out
        // (see above), and it is not this one.
        restriction={
          pendingRow
            ? null
            : gateStatusRestriction({
                currentStatus: checkIn?.status,
                orderBound,
                isNextInQueue,
                nextLabel,
              })
        }
      />
      <GateDockField editor={editor} />
    </Box>
  );
};
